import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { UserInfo } from '@/lib/types';

type Field = {
  key: keyof UserInfo;
  label: string;
  placeholder: string;
  type?: string;
  hint?: string;
};

const FIRMA_FIELDS: Field[] = [
  { key: 'jmeno', label: 'Jméno', placeholder: 'Jan Realiťák' },
  { key: 'firma', label: 'Firma / RK (volitelné)', placeholder: 'RK ABC' },
  { key: 'telefon', label: 'Telefon', placeholder: '+420 777 123 456', type: 'tel' },
  { key: 'email', label: 'E-mail', placeholder: 'jan@realitka.cz', type: 'email' },
  { key: 'misto', label: 'Město odesílatele', placeholder: 'Brno', hint: 'Pro placeholder „V {moje_misto} dne {datum}".' },
  { key: 'adresa', label: 'Adresa (volitelné)', placeholder: 'Pekařská 12, 60200 Brno' },
];

const OSOBNI_FIELDS: Field[] = [
  { key: 'osobniJmeno', label: 'Jméno (osobní)', placeholder: 'Honza', hint: 'Volitelné, pokud nechceš podepsat plným jménem.' },
  { key: 'osobniTelefon', label: 'Telefon (osobní)', placeholder: '+420 608 000 000', type: 'tel' },
  { key: 'osobniEmail', label: 'E-mail (osobní)', placeholder: 'honza@gmail.com', type: 'email' },
];

export default function Settings() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<UserInfo | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const query = useQuery({
    queryKey: ['settings', 'user-info'],
    queryFn: () => api<{ ok: true; userInfo: UserInfo }>('/api/settings/user-info'),
  });

  useEffect(() => {
    if (query.data && !draft) {
      setDraft(query.data.userInfo);
    }
  }, [query.data, draft]);

  const save = useMutation({
    mutationFn: (value: UserInfo) =>
      api<{ ok: true; userInfo: UserInfo }>('/api/settings/user-info', {
        method: 'PUT',
        json: value,
      }),
    onSuccess: (data) => {
      qc.setQueryData(['settings', 'user-info'], data);
      setSavedAt(new Date());
    },
  });

  if (!draft) {
    return <p className="text-sm text-muted-foreground">Načítám…</p>;
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Nastavení</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Tvoje údaje se používají v dopisech přes placeholdery jako{' '}
          <code className="text-xs bg-muted px-1 rounded">{'{moje_jmeno}'}</code>,{' '}
          <code className="text-xs bg-muted px-1 rounded">{'{moje_telefon}'}</code> atd.
        </p>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(draft);
        }}
        className="space-y-6"
      >
        <FieldGroup title="Firemní odesílatel" fields={FIRMA_FIELDS} draft={draft} setDraft={setDraft} />

        <FieldGroup
          title="Osobní identita"
          subtitle={'Použije se u šablon označených „Osobní dopis". Tam se vynechá firemní hlavička a placeholdery {moje_telefon} / {moje_email} se nahradí těmito hodnotami.'}
          fields={OSOBNI_FIELDS}
          draft={draft}
          setDraft={setDraft}
        />

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={save.isPending}
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
          >
            {save.isPending ? 'Ukládám…' : 'Uložit'}
          </button>
          {savedAt && (
            <span className="text-xs text-muted-foreground">
              Uloženo {savedAt.toLocaleTimeString('cs-CZ')}.
            </span>
          )}
          {save.error && (
            <span className="text-xs text-destructive">Chyba: {String(save.error)}</span>
          )}
        </div>
      </form>
    </div>
  );
}

function FieldGroup({
  title,
  subtitle,
  fields,
  draft,
  setDraft,
}: {
  title: string;
  subtitle?: string;
  fields: Field[];
  draft: UserInfo;
  setDraft: (u: UserInfo) => void;
}) {
  return (
    <section className="rounded-lg border">
      <div className="px-4 py-2.5 border-b bg-muted/40">
        <div className="text-sm font-medium">{title}</div>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      <div className="p-4 space-y-4">
        {fields.map((f) => (
          <div key={f.key} className="space-y-1.5">
            <label className="text-sm font-medium">{f.label}</label>
            <input
              type={f.type || 'text'}
              value={draft[f.key] ?? ''}
              onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
              placeholder={f.placeholder}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
            {f.hint && <p className="text-xs text-muted-foreground">{f.hint}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}
